import {
  AfterViewInit,
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  TemplateRef,
  Optional,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialUiModule } from '@deltager/organizers/admin/core/material-ui/material-ui.module';
import { CommonModule } from '@angular/common';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-base-dialog',
  templateUrl: './base-dialog.component.html',
  styleUrl: './base-dialog.component.scss',
  standalone: true,
  imports: [CommonModule, TranslateModule, MaterialUiModule],
})
export class BaseDialogComponent implements AfterViewInit {
  @Input({ required: true }) title: TemplateRef<void> | string;
  @Input() cancelDisabledCondition: boolean;
  @Input() saveDisabledCondition: boolean;
  @Input() saveBtnTextCodeOrTemplate: string | TemplateRef<void> = 'global.save';
  @Input() cancelBtnTextCode: string = 'global.cancel';
  @Input({ transform: booleanAttribute }) overrideCancelBtnBehavior: boolean;
  @Input() hideSaveBtn: boolean;
  @Input() hideCancelBtn: boolean;
  @Input() isLoading$ = signal<boolean>(false);
  @Input() customSaveBtnClass: string;

  @Output() save = new EventEmitter<void>();
  @Output() closeDialog = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  constructor(
    private el: ElementRef,
    @Optional() private dialogRef: MatDialogRef<BaseDialogComponent>,
  ) {
    // Listen for backdrop clicks if dialogRef is available
    if (this.dialogRef) {
      this.dialogRef.backdropClick().subscribe(() => {
        this.closeDialog.emit();
      });
    }
  }

  get isTitleTemplate(): boolean {
    return this.title instanceof TemplateRef;
  }

  get isSaveBtnTemplate(): boolean {
    return this.saveBtnTextCodeOrTemplate instanceof TemplateRef;
  }

  public ngAfterViewInit(): void {
    this.restoreFocusUnderAriaHidden();
  }

  public onCancel(): void {
    if (this.overrideCancelBtnBehavior) {
      this.cancel.emit();
      return;
    }

    this.closeDialog.emit();
  }

  private restoreFocusUnderAriaHidden(): void {
    const dialogContainer = this.el.nativeElement.querySelector('.dialog-container');
    const parentWithAriaHidden = dialogContainer.closest('[aria-hidden]');

    if (parentWithAriaHidden) {
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.attributeName === 'aria-hidden' && parentWithAriaHidden.getAttribute('aria-hidden') === 'true') {
            console.warn('aria-hidden applied! Moving focus...');
            dialogContainer.blur();
            document.body.focus();
          }
        });
      });

      observer.observe(parentWithAriaHidden, { attributes: true });
    }
  }
}
